import { ClientPresenter } from '@shared/modules/clients/client.presenter';
import { TeamPresenter } from '@shared/modules/teams/team-presenter';
import { Presenter } from '@shared/presenters/presenter';
import { isAfter, isSameOrBefore } from '@shared/utils/date-time';
import { readableDateRange } from '@shared/utils/dates/readable-date-range';
import { niceList } from '@shared/utils/strings';
import { DateTime } from 'luxon';
import type { ServiceContract } from './service-contract';
import { BuyerPresenter } from '../buyers/buyer.presenter';
import { UserPresenter } from '../users/user.presenter';

export class ServiceContractPresenter extends Presenter<ServiceContract> {
    get client(): ClientPresenter {
        return this.present('client.data', ClientPresenter);
    }

    get name() {
        return this.data.description;
    }

    get startDateTime() {
        return this.toDateTime('start', false);
    }

    get startDate(): string {
        return this._get('startDate', () => this.formatDate(this.startDateTime));
    }

    get finishDateTime() {
        return this.toDateTime('finish', false);
    }

    get finishDate(): string {
        return this._get('finishDate', () => this.formatDate(this.finishDateTime));
    }

    get plannedStartDateTime() {
        return this.toDateTime('planned_start', false);
    }

    get plannedStartDate() {
        return this._get('plannedStartDate', () => this.formatDate(this.plannedStartDateTime));
    }

    get plannedFinishMoment() {
        return this.toDateTime('planned_finish', false);
    }

    get plannedFinishDate() {
        return this._get('plannedFinishDate', () => this.formatDate(this.plannedFinishMoment));
    }

    get dateRange() {
        return this._get('dateRange', () => {
            const start = this.startDateTime;
            const finish = this.finishDateTime;

            if (!start) {
                return;
            }

            if (!finish) {
                return `${this.formatDateShort(start)} &rarr; continuing`;
            }

            return readableDateRange(start, finish);
        });
    }

    isActive(date: DateTime = DateTime.local()) {
        date = (date instanceof DateTime ? date.toLocal() : DateTime.local()).startOf('day');

        return this._get(`isActive:${date.toFormat('yyyy-MM-dd')}`, () => {
            const start = this.startDateTime;
            const finish = this.finishDateTime;

            if (!start && !finish) {
                return false;
            }

            if (isAfter(start, date)) {
                return false;
            }

            return !(finish && isSameOrBefore(finish, date));
        });
    }

    isInactive(date = DateTime.local()) {
        return !this.isActive(date);
    }

    isFinished(date: DateTime = DateTime.local()) {
        date = (date instanceof DateTime ? date.toLocal() : DateTime.local()).startOf('day');

        return this._get(`isFinished:${date.toFormat('yyyy-MM-dd')}`, () => {
            const finish = this.finishDateTime;

            if (!finish) {
                return false;
            }

            return isSameOrBefore(finish, date, 'day');
        });
    }

    isPending(date = DateTime.local()) {
        const start = this.startDateTime;

        if (!start) {
            return false;
        }

        date = (date instanceof DateTime ? date.toLocal() : DateTime.local()).startOf('day');

        return isAfter(start, date);
    }

    // get type() {
    //     return this._get('type', () => {
    //         return ServiceContractTypePresenter.create(this.get('type.data'));
    //     });
    // }

    get description() {
        return this.data.description;
    }

    // get rounding() {
    //     return this._get('rounding', () => {
    //         if (this.get('duration_rounding_overridden')) {
    //             return DurationRounding.fromObject(this.get('duration_rounding'));
    //         }
    //
    //         return DurationRounding.fromObject(this.get('type.data.duration_rounding'));
    //     });
    // }

    get teams() {
        return this.present('teams', TeamPresenter);
    }

    get teamDescriptions() {
        return this._get('teamDescriptions', () => niceList(this.teams.map(team => team.description)));
    }

    get effectiveBuyers() {
        return this._get(
            'effectiveBuyer',
            () => BuyerPresenter.attach(this.get('effective_option_set.data.buyers.data') ?? [])
        );
    }

    get lastEditedBy() {
        return this._get('lastEditedBy', () => {
            const data = this.data.last_edited_by?.data;
            return data ? UserPresenter.create(data) : undefined;
        });
    }
}
